import InfoIcon from '@mdi/svg/svg/information-outline.svg?react';
import { IconWithTooltip } from '@solinkcorp/solink-components';
import classNames from 'classnames';
import { type NewActivityType } from 'components/ScheduledActivityForm/useScheduledEventForm';
import BouncingDots from 'loadingIndicator/BouncingDots';
import React from 'react';
import useLocale from 'src/hooks/useLocale';
import { useSelector } from 'stores/store';
import LocaleUtil from 'utils/LocaleUtil';
import { MAX_NUMBER_OF_SELECTED_CAMERAS } from 'vd-constants';
import { useScheduledSpotCheckMonthlyUsage } from '../hooks/useScheduledSpotCheckMonthlyUsage';
import styles from './SpotCheckFooter.module.sass';

interface SpotCheckFooterProps {
  newActivity: NewActivityType;
  view: 'templates' | 'spot-check-form';
  totalEventsInLast30Days: number | undefined;
  isEventsEstimateBelowLimit: boolean;
  availableQuota: number | undefined;
  displayLoadingQuota: boolean;
  estimatedTotalIsolatedRemainingQuota: number | undefined;
  configuredTotalQuota: number | undefined;
}

export default function SpotCheckFooter(props: SpotCheckFooterProps) {
  const {
    newActivity,
    totalEventsInLast30Days,
    isEventsEstimateBelowLimit,
    availableQuota,
    displayLoadingQuota,
    estimatedTotalIsolatedRemainingQuota,
    configuredTotalQuota,
  } = props;
  const locale = useLocale();

  const partialSpotCheck = React.useMemo(
    () => ({
      startsAt: newActivity.startsAt,
      endsAt: newActivity.endsAt,
      reoccurringCount: newActivity.reoccurringCount,
      rule: newActivity.rule,
      cameraIds: newActivity.cameraIds,
    }),
    [
      newActivity.startsAt,
      newActivity.endsAt,
      newActivity.reoccurringCount,
      newActivity.rule,
      newActivity.cameraIds,
    ],
  );

  const spotCheckMode = useSelector((state) => state.filter.spotCheckMode);

  const isLivePreviewMode = spotCheckMode === 'live-preview';
  const isEventSpotCheck = spotCheckMode === 'event';
  const isScheduledSpotCheck = spotCheckMode === 'scheduled';

  const { monthlyUsage: scheduledSpotCheckMonthlyUsage } = useScheduledSpotCheckMonthlyUsage(
    isScheduledSpotCheck ? partialSpotCheck : undefined,
  );

  const spotCheckMonthlyUsage = isScheduledSpotCheck
    ? scheduledSpotCheckMonthlyUsage
    : totalEventsInLast30Days;

  const isMonthlyOverRemaining =
    spotCheckMonthlyUsage != null &&
    availableQuota != null &&
    spotCheckMonthlyUsage > availableQuota;

  const numberOfSelectedCameras = newActivity.cameraIds.length;

  const spotCheckLivePreviewNumberOfCamerasText = LocaleUtil.getLocalizedStr({
    string: locale.scheduledAIChat.spotCheckLivePreviewNumberOfCameras,
    replacements: {
      current: numberOfSelectedCameras,
      maximumNumber: MAX_NUMBER_OF_SELECTED_CAMERAS,
    },
  });

  const spotCheckLivePreviewNumberOfCamerasInfo = LocaleUtil.getLocalizedStr({
    string: locale.scheduledAIChat.spotCheckLivePreviewNumberOfCamerasInfo,
    replacements: { maximumNumber: MAX_NUMBER_OF_SELECTED_CAMERAS },
  });

  const spotCheckRemainingBalanceText = LocaleUtil.getLocalizedStr({
    string:
      estimatedTotalIsolatedRemainingQuota !== undefined
        ? locale.scheduledAIChat.spotCheckRemainingIsolatedBalance
        : locale.scheduledAIChat.spotCheckRemainingBalance,
    replacements: { remaining: availableQuota },
  });

  const scheduledSpotCheckEstimatedUsageText = LocaleUtil.getLocalizedStr({
    string: locale.scheduledAIChat.spotCheckEstimatedUsage,
    replacements: { usage: spotCheckMonthlyUsage },
  });

  const eventSpotCheckEstimatedUsageText = LocaleUtil.getLocalizedStr({
    string: locale.scheduledAIChat.spotCheckEstimatedUsage,
    replacements: { usage: totalEventsInLast30Days },
  });

  const isNegativeAvailableQuota = availableQuota != null && availableQuota < 0;

  const displaySpotCheckUsage = !isLivePreviewMode && !!spotCheckMonthlyUsage;

  const displayAvailableQuota =
    (isEventSpotCheck || isScheduledSpotCheck) && availableQuota != null;

  const spotCheckQuotaInfoText = isEventSpotCheck
    ? locale.scheduledAIChat.eventSpotCheckBalanceInfo
    : locale.scheduledAIChat.scheduledSpotCheckBalanceInfo;

  const isolatedUsageInfoText = LocaleUtil.getLocalizedStr({
    string: locale.scheduledAIChat.spotCheckIsolatedBalanceInfo,
    replacements: { quota: configuredTotalQuota },
  });

  const usageInfoText =
    (estimatedTotalIsolatedRemainingQuota !== undefined
      ? isolatedUsageInfoText
      : spotCheckQuotaInfoText) +
    ' ' +
    (isNegativeAvailableQuota
      ? locale.scheduledAIChat.clearUpSpotChecks
      : locale.scheduledAIChat.spotCheckBalanceResetInfo);

  const spotCheckUsageInfoText = isEventSpotCheck
    ? locale.scheduledAIChat.eventSpotCheckUsageInfo
    : locale.scheduledAIChat.scheduledSpotCheckUsageInfo;

  const showEstimatedUsageError = !isEventsEstimateBelowLimit || isMonthlyOverRemaining;

  if (displayLoadingQuota) {
    return (
      <div className={styles.spotCheckFooter}>
        <BouncingDots className={styles['balance-bouncing-dots']} />
      </div>
    );
  }

  return (
    <div className={styles.spotCheckFooter}>
      {isLivePreviewMode && !!numberOfSelectedCameras && (
        <>
          <div>{spotCheckLivePreviewNumberOfCamerasText}</div>
          <IconWithTooltip
            title={spotCheckLivePreviewNumberOfCamerasInfo}
            icon={InfoIcon}
            fill="#CCCCCC"
            iconSize={20}
          />
        </>
      )}
      {/* For when you have available quota */}
      {displayAvailableQuota && (
        <>
          <div className={classNames({ [styles.error]: isNegativeAvailableQuota })}>
            {spotCheckRemainingBalanceText}
          </div>
          <IconWithTooltip
            title={usageInfoText}
            icon={InfoIcon}
            fill={isNegativeAvailableQuota ? 'var(--color-red)' : '#CCCCCC'}
            iconSize={20}
          />
        </>
      )}
      {/* For any spot check mode that has an estimated usage (currently Scheduled and Event) */}
      {displaySpotCheckUsage && displayAvailableQuota && (
        <div className={styles.spotCheckBalanceSeparator}></div>
      )}
      {displaySpotCheckUsage && (
        <>
          <div
            className={classNames({
              [styles.error]: showEstimatedUsageError,
            })}
            data-testid={showEstimatedUsageError ? 'spot-check-estimated-usage-error' : undefined}
          >
            {isEventSpotCheck
              ? eventSpotCheckEstimatedUsageText
              : scheduledSpotCheckEstimatedUsageText}
          </div>
          <IconWithTooltip
            title={
              isMonthlyOverRemaining
                ? locale.scheduledAIChat.insufficientSpotCheckBalance
                : spotCheckUsageInfoText
            }
            icon={InfoIcon}
            fill={isMonthlyOverRemaining ? 'var(--color-red)' : '#CCCCCC'}
            iconSize={20}
          />
        </>
      )}
    </div>
  );
}
